import { Component, OnInit, Input, EventEmitter, Output } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { SelectionModel } from '@angular/cdk/collections';
import { Observable, BehaviorSubject, filter } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { IonicModule } from '@ionic/angular';
import { Comparable, Property } from '@smartflip/data-models';
import { convertPointStringToLatLong } from '@smartflip/utils-data';
import { PropertyService } from '@smartflip/helper-utils';
import { filterControlMetadata } from '@smartflip/data-field-configuration';
import {
  ComparablesHelperService,
  InteractiveMapModule,
  MapLocation,
  updateFilters,
} from '@smartflip/interactive-map';
import {
  ComparablesService,
  PullComparablesService,
} from '@smartflip/service-comparables';
import {
  calculateARVButtonText,
  comparablesInstructions,
  comparablesWarningMessage,
  enterAddressWarning,
  pullCompsButtonText,
  refreshCompsButtonText,
  selectCompsForARVText,
} from './comparables.constants';
import { PropertyDetailsService } from '../../../../../../apps/app/src/app/views/property-details/property-details.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ComparablesFilterActionsComponent } from './comparables-filter-actions/comparables-filter-actions.component';
import { ComparablesTableComponent } from './comparables-table/comparables-table.component';
import { ComparablesHeaderComponent } from './comparables-header/comparables-header.component';

@Component({
  selector: 'app-comparables',
  templateUrl: './comparables.component.html',
  styleUrls: ['./comparables.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    ComparablesFilterActionsComponent,
    ComparablesTableComponent,
    ComparablesHeaderComponent,
    InteractiveMapModule,
  ],
})
export class ComparablesComponent implements OnInit {
  @Input() error!: boolean;
  @Output() errorChange: EventEmitter<boolean> = new EventEmitter();

  public arvIsSet: boolean = false;
  public comparablesInstructions = comparablesInstructions;
  public comparablesWarningMessage = comparablesWarningMessage;
  public calculateARVButtonText = selectCompsForARVText;
  public compsList$: BehaviorSubject<null | Comparable[]> = new BehaviorSubject<
    null | Comparable[]
  >(null);
  public currentPropertyLabel!: string;
  public compsUrl!: SafeResourceUrl;
  public enterAddressWarning = enterAddressWarning;
  public errorText!: string;
  public isNative: boolean = false;
  public propertyIsSet: boolean = false;
  public loadingComps$: Observable<boolean> =
    this.pullCompsService.loadingComps$;
  public getCompsButtonText = pullCompsButtonText;
  public selectedComps: Comparable[] = [];
  public showMap: boolean = true;
  public showMapToggle: boolean = true;
  public currentFilters!: any[];
  public highlightedAddress$: BehaviorSubject<string> = new BehaviorSubject('');
  public advancedMarkers!: google.maps.marker.AdvancedMarkerElement[];
  public displayAddress$ = this.propertyDetailService.displayAddress$;
  property!: Property;
  location: number[] | null = [0, 0];
  comparablesMarkers!: MapLocation[];
  hoveredComp?: string;

  constructor(
    private comparablesService: ComparablesService,
    private comparablesHelperService: ComparablesHelperService,
    private pullCompsService: PullComparablesService,
    private propertyService: PropertyService,
    private propertyDetailService: PropertyDetailsService,
  ) {}

  public async pullComps(optionalFilters: Record<string, unknown>) {
    if (!this.propertyIsSet) return;

    if (!optionalFilters) {
      optionalFilters =
        this.propertyService.currentProperty?.compsFilters || {};
    }

    const compsResults = await this.pullCompsService.pullComps(optionalFilters);

    this.setCurrentFilters(optionalFilters);
    this.comparablesService.setComps(compsResults);
    this.getCompsButtonText = refreshCompsButtonText;
  }

  public updateCompsSelected(selectedComps: SelectionModel<Comparable>): void {
    this.selectedComps = selectedComps.selected;
    this.calculateARVButtonText =
      this.selectedComps.length > 0
        ? calculateARVButtonText
        : selectCompsForARVText;
  }

  public highlightPropertyInList(marker: MapLocation) {
    this.highlightedAddress$.next(marker?.label || '');
  }

  get addressIsValid(): boolean {
    const { address, city, state, zip } =
      this.propertyService.currentProperty?.gen || {};

    return (
      address.length > 5 &&
      city.length > 0 &&
      state.length > 0 &&
      zip.length > 0
    );
  }

  ngOnInit() {
    if (Capacitor.isNativePlatform()) {
      this.showMapToggle = false; // Hide the check box
      this.showMap = false; // Always hide the map on native until we fix it
      this.isNative = true;
    }
    this.propertyDetailService.propertyIsSet$.subscribe((propertyIsSet) => {
      if (!propertyIsSet) return;

      this.propertyIsSet = propertyIsSet;
      this.property = this.propertyService.currentProperty;

      const {
        compsFilters,
        gen: { address, geoLocation },
      } = this.property;

      this.location = convertPointStringToLatLong(geoLocation as string);
      this.currentPropertyLabel = address;
      this.setCurrentFilters(compsFilters);

      this.comparablesService.init();
      this.comparablesService.comps$
        .pipe(filter((comp) => !!comp))
        .subscribe(this.setupComparables.bind(this));
      this.comparablesHelperService.advancedMarkers$.subscribe(
        (markers) => (this.advancedMarkers = markers),
      );
    });
  }

  private setupComparables(comparablesList: Comparable[]) {
    this.compsList$.next(comparablesList);
    this.getCompsButtonText =
      comparablesList?.length > 0
        ? refreshCompsButtonText
        : pullCompsButtonText;
    this.comparablesHelperService
      .setComparablesMarkers(comparablesList, this.location)
      .then((newMakers) => this.handleMarkersSetup(newMakers));
  }

  private handleMarkersSetup(newMarkers: MapLocation[]) {
    const centerOfMap = {
      lat: this.location?.[0] || 0,
      lng: this.location?.[1] || 0,
    };

    this.comparablesMarkers = newMarkers;
    // pass along this information for later use in the component
    this.comparablesHelperService.createAdvancedMarkers(
      this.comparablesMarkers,
      centerOfMap,
    );
  }

  onHoverChange(comp?: Comparable) {
    this.advancedMarkers?.map((marker) => {
      const markerContent = marker.content as any;

      markerContent
        .querySelector('.highlighted')
        ?.classList.remove('highlighted');

      const markerAddress =
        markerContent.querySelector('.map-label').textContent;

      if (comp?.street === markerAddress) {
        markerContent
          .querySelector('.material-symbols-outlined')
          .classList.add('highlighted');
      }
    });
  }

  // Show the filters previously selected if applicable
  // Filter values can be at least, string, number, string[] or date so using unknown here
  setCurrentFilters(
    filters: Record<keyof typeof filterControlMetadata, unknown> | null = null,
  ) {
    this.currentFilters = filters ? updateFilters(filters) : [];
  }

  public toggleMap() {
    this.showMap = !this.showMap;
  }
}
